import {
  isRecord,
  nullableString,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'
import type { Replace } from '@/lib/type-utils'

type Schemas = components['schemas']

export type EntityHistoryActorKind = 'user' | 'external' | 'system' | 'scim'

export type EntityHistoryActor = Replace<Schemas['EntityHistoryActorResponse'], 'kind', {
  kind: EntityHistoryActorKind
}>

function decodeActorKind(raw: unknown): EntityHistoryActorKind {
  const kind = requireString(raw, 'actor.kind')
  if (kind === 'user' || kind === 'external' || kind === 'system' || kind === 'scim') return kind
  throw new Error(`Unexpected actor kind "${kind}"`)
}

export const OUT_OF_SCOPE_ACTOR = 'Someone outside your organization'

export function describeActor(actor: EntityHistoryActor): string {
  switch (actor.kind) {
    case 'user':
      return actor.displayName ?? OUT_OF_SCOPE_ACTOR
    case 'scim':
      return 'SCIM provisioning'
    case 'system':
      return 'System'
    default:
      return actor.entityName ? `Someone at ${actor.entityName}` : OUT_OF_SCOPE_ACTOR
  }
}

function decodeMessageParams(raw: unknown): Record<string, string> | null {
  if (raw === null || raw === undefined) return null
  if (!isRecord(raw)) throw new Error('Malformed message params')
  const params: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw))
    params[key] = requireString(value, `messageParams.${key}`)
  return params
}

export type ActivityEntryBase = Replace<Schemas['EntityHistoryEntryResponse'], 'actor', {
  actor: EntityHistoryActor
}>

export function decodeActivityEntryBase(
  raw: Record<string, unknown>,
): ActivityEntryBase {
  if (!isRecord(raw.actor)) throw new Error('Malformed activity actor')
  return {
    id: requireString(raw.id, 'id'),
    timestamp: requireString(raw.timestamp, 'timestamp'),
    action: requireString(raw.action, 'action'),
    messageKey: requireString(raw.messageKey, 'messageKey'),
    messageParams: decodeMessageParams(raw.messageParams),
    origin: requireString(raw.origin, 'origin'),
    aiChannel: nullableString(raw.aiChannel, 'aiChannel'),
    aiOperation: nullableString(raw.aiOperation, 'aiOperation'),
    actor: {
      id: requireString(raw.actor.id, 'actor.id'),
      type: requireString(raw.actor.type, 'actor.type'),
      kind: decodeActorKind(raw.actor.kind),
      displayName: nullableString(raw.actor.displayName, 'actor.displayName'),
      entityName: nullableString(raw.actor.entityName, 'actor.entityName'),
    } satisfies EntityHistoryActor,
  } satisfies ActivityEntryBase
}
